import { RefObject, useCallback, useEffect } from 'react';

/* -- HOW TO USE: --
 *
 * const containerRef = useRef(null);
 *
 * useOutsideClickHook(containerRef, (event) => {
 *   setIsOpen(false);
 * });
 *
 * <div ref={containerRef} />
 */

/**
 * @param ref
 * @param callback
 * @param observe - ключ, который сообщает, что нужно начать следить за ref.
 * Небольшая оптимизация, которая не будет вызывать callback, если ref скрыт в DOM дереве.
 */
const useOutsideClick = (
  ref: RefObject<HTMLElement> | RefObject<HTMLElement>[],
  callback: (event: MouseEvent) => void,
  observe = true,
): void => {
  const handleClick = useCallback((event: MouseEvent): void => {
    if (!observe) {
      return;
    }

    if (Array.isArray(ref)) {
      for (const refElement of ref) {
        if (refElement.current?.contains(event.target as HTMLElement)) {
          return;
        }
      }
    }
    else if (ref.current?.contains(event.target as HTMLElement)) {
      return;
    }

    callback(event);
  }, [callback, observe]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClick, true);

    return (): void => {
      document.removeEventListener('mousedown', handleClick, true);
    };
  });
};

export default useOutsideClick;
